"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { useQuery } from "@tanstack/react-query"

import { ApolloPageShell, ApolloPageHeader } from "@/components/ui/apollo-page"
import {
  StatsMetaline,
  type StatsMetalineItem,
} from "@/components/ui/stats-metaline"
import { Skeleton } from "@/components/ui/skeleton"
import { useRouter } from "next/navigation"

import { AffiliatorIntakeCard } from "@/components/dashboard/affiliator-intake-card"
import { TodayScheduleTimeline } from "@/components/dashboard/today-schedule-timeline"
import { NeedsBookingFilters } from "@/components/dashboard/variants/frontdesk/needs-booking-filters"
import { NeedsBookingTable } from "@/components/dashboard/variants/frontdesk/needs-booking-table"
import { needsOf, useNeedsBooking } from "@/components/dashboard/variants/frontdesk/use-needs-booking"
import { TravelDeskCard } from "@/components/travel-desk/travel-desk-card"
import { usePermissions } from "@/hooks/use-permissions"
import { getAffiliatorIntakes } from "@/lib/affiliator-intake-api"
import { hasFlag } from "@/lib/feature-flags"
import { useAuthStore } from "@/stores/auth-store"
import { useRemindersStore } from "@/stores/reminders-store"

import {
  isVisibleAppointmentType,
  presetAppointmentTypes,
} from "@/components/calendar/calendar-modes"
import { getAppointmentStats } from "@/lib/appointments-api"
import { getCalendarEvents } from "@/lib/calendar-events"
import type { AppointmentStats } from "@/types"

/** Local `YYYY-MM-DDTHH:mm:ss` — no timezone suffix, matching the calendar page. */
function localStamp(date: Date, time: string): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}T${time}`
}

/**
 * The `flight_checkin_due` bell's default window (Radiance 261001): the
 * logistics header's "not checked in" count looks the same distance ahead, so
 * the number and the bell agree on who is due.
 */
const CHECK_IN_ALERT_HOURS = 28

/** The desk layout's panels, as scroll targets for the attention line. */
const PANEL_ID = {
  board: "desk-board",
  partners: "desk-partners",
} as const

/** Scroll a panel into view — the attention line's whole job. */
function jumpTo(id: string): () => void {
  return () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
}

interface CoordinatorData {
  appointmentStats: AppointmentStats | null
  /** Tomorrow's calendar feed — only its length is used (a tomorrow count). */
  tomorrowCount: number | null
}

const EMPTY_DATA: CoordinatorData = {
  appointmentStats: null,
  tomorrowCount: null,
}

/**
 * Coordinator dashboard — "Today's clinic".
 *
 * The front desk owns the day's appointment flow: which chairs are running now,
 * which booking requests are still waiting on a human, and what the desk has
 * collected. So the headline band is a clinic-DAY band (today / pending
 * approval / this week / tomorrow) rather than the generic leads-and-quotes
 * strip, the hero is the whole clinic's schedule (no `userId` — a coordinator
 * runs everyone's day), and the approval queue sits beside it as a worklist.
 *
 * Two layouts, chosen by `coordinator_desk_layout_enabled`:
 *
 *  - OFF (every clinic as before): one column — the partner panel, the board,
 *    today's schedule, the Travel Desk — under an appointments band.
 *  - ON (ArtDent 261015, "the coordinator must not miss anything"): the header
 *    carries an ATTENTION LINE instead — today's visits, pending board rows,
 *    unopened partner patients, unread alerts — each a click to where it is
 *    handled and all zero at the end of a good day; the body is ONE full-width
 *    column: the board, then the partner table (owner order 261015 — the
 *    queue first; the flag-off layout keeps the 261007 partner-first order).
 *    Nothing else: no Today's schedule, no Travel Desk, no Alerts
 *    panel (owner decisions 261015 — a side column with the schedule was
 *    built and taken out the same day). The schedule lives on the calendar,
 *    which the Today count opens; the roster keeps its own page; the alerts
 *    count opens Notifications.
 *
 * Permission contract: nothing is requested until `usePermissions().isLoaded`,
 * every call is skipped when its grant is missing, and a widget whose data was
 * never fetched is OMITTED rather than rendered empty (hide-not-disable).
 */
export function CoordinatorDashboard() {
  const t = useTranslations("dashboard")
  const { isLoaded, can } = usePermissions()
  const user = useAuthStore((s) => s.user)
  const router = useRouter()
  const deskLayout = hasFlag(user?.feature_flags, "coordinator_desk_layout_enabled")

  // One signal for "an appointment changed" on this page. The board and Today's
  // Schedule each fetch on their own, so booking from one left the other stale
  // until a reload: a patient booked from the board never appeared in today's
  // schedule, and a visit cancelled from the schedule never reopened its row.
  // Either surface bumps this; both — and the KPI band — reload on it.
  const [appointmentsVersion, setAppointmentsVersion] = useState(0)
  const bumpAppointments = useCallback(() => setAppointmentsVersion((v) => v + 1), [])

  // The desk board, shared with the front-desk variant. Called unconditionally
  // (hooks rule) — it self-gates internally and reports `allowed`.
  const needsBooking = useNeedsBooking({
    refreshKey: appointmentsVersion,
    onAppointmentsChanged: bumpAppointments,
  })

  const canViewAppointments = can("appointments.view")
  // Radiance 261001 — the logistics desk books nothing, so its header counts
  // transport rather than appointments (see transportItems below).
  const logisticsDesk = needsBooking.checkInMode
  const showAppointmentBand = canViewAppointments && !logisticsDesk

  // Preset narrowing: the coordinator preset declares a visits board
  // (default_types without lead_call), so every appointment surface on this
  // dashboard — KPI band, tomorrow count, timeline — hides call-centre rows.
  // Null (no narrowing) on clinics whose preset carries every intent.
  const calendarPresets = useAuthStore((s) => s.user?.calendar_presets)
  const allowedTypes = useMemo(
    () => presetAppointmentTypes(calendarPresets),
    [calendarPresets],
  )
  // `/appointments/stats` filters server-side but takes a single intent — only
  // expressible when the preset narrows to exactly one.
  const statsType = allowedTypes?.length === 1 ? allowedTypes[0] : undefined

  const [data, setData] = useState<CoordinatorData>(EMPTY_DATA)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Hold every request until the permission set has resolved — firing first
    // and discarding would 403 for users who legitimately lack the grant.
    if (!isLoaded) return

    let cancelled = false
    const load = async () => {
      // A reload after an appointment change keeps the current numbers on
      // screen instead of blanking the band to skeletons for every booking.
      if (appointmentsVersion === 0) setIsLoading(true)

      // Tomorrow's window in local wall-clock (the calendar endpoint takes
      // naive local stamps); only the row count is surfaced.
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)

      try {
        const [appointmentStats, tomorrowEvents] = await Promise.all([
          showAppointmentBand
            ? getAppointmentStats(statsType).catch(() => null)
            : Promise.resolve(null),
          showAppointmentBand
            ? getCalendarEvents(
                localStamp(tomorrow, "00:00:00"),
                localStamp(tomorrow, "23:59:59"),
              ).catch(() => null)
            : Promise.resolve(null),
        ])
        if (!cancelled) {
          const tomorrowRows = tomorrowEvents
            ? (tomorrowEvents.data ?? []).filter((event) =>
                isVisibleAppointmentType(event.type, allowedTypes),
              )
            : null
          setData({
            appointmentStats,
            tomorrowCount: tomorrowRows ? tomorrowRows.length : null,
          })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [isLoaded, showAppointmentBand, statsType, allowedTypes, appointmentsVersion])

  const appointmentItems = useMemo<StatsMetalineItem[]>(() => {
    // Every tile in this band is appointment-derived, so the whole band is
    // omitted for a user without `appointments.view`.
    if (!showAppointmentBand) return []

    const skeletonValue = <Skeleton className="h-[13px] w-12" />

    return [
      {
        key: "today",
        label: t("coordinatorKpi.today"),
        value: isLoading ? skeletonValue : (data.appointmentStats?.today ?? 0),
        sub: isLoading ? undefined : t("coordinatorKpi.todayHint"),
      },
      {
        key: "thisWeek",
        label: t("coordinatorKpi.thisWeek"),
        value: isLoading ? skeletonValue : (data.appointmentStats?.this_week ?? 0),
        sub: isLoading ? undefined : t("coordinatorKpi.thisWeekHint"),
      },
      {
        key: "tomorrow",
        label: t("coordinatorKpi.tomorrow"),
        value: isLoading ? skeletonValue : (data.tomorrowCount ?? 0),
        sub: isLoading ? undefined : t("coordinatorKpi.tomorrowHint"),
      },
    ]
  }, [showAppointmentBand, isLoading, data, t])

  /**
   * The logistics desk's header (Radiance 261001): what the transport day
   * holds, counted from the board's own rows — every trip in the window, done
   * or not — so it costs no request and never disagrees with the table.
   */
  const transportItems = useMemo<StatsMetalineItem[]>(() => {
    if (!logisticsDesk) return []

    const loading = needsBooking.isLoading
    const skeletonValue = <Skeleton className="h-[13px] w-12" />
    const rows = needsBooking.rows
    const now = Date.now()
    // The flight is 24 hours after check-in opens; "soon" is the bell's window.
    const flightSoon = (opensAt: Date | null): boolean => {
      if (opensAt === null) return false
      const flightAt = opensAt.getTime() + 24 * 3_600_000
      return flightAt > now && flightAt - now <= CHECK_IN_ALERT_HOURS * 3_600_000
    }

    const count = (predicate: (row: (typeof rows)[number]) => boolean) =>
      loading ? skeletonValue : rows.filter(predicate).length

    return [
      {
        key: "arrivals",
        label: t("logisticsKpi.arrivals"),
        value: count((r) => r.reason === "arriving" && r.diffDays === 0),
        sub: loading ? undefined : t("logisticsKpi.arrivalsHint"),
      },
      {
        key: "departures",
        label: t("logisticsKpi.departures"),
        value: count((r) => r.reason === "departing" && r.diffDays === 0),
        sub: loading ? undefined : t("logisticsKpi.departuresHint"),
      },
      {
        key: "notCheckedIn",
        label: t("logisticsKpi.notCheckedIn"),
        value: count((r) => needsOf(r).checkIn && flightSoon(r.checkIn?.opensAt ?? null)),
        sub: loading ? undefined : t("logisticsKpi.notCheckedInHint", { hours: CHECK_IN_ALERT_HOURS }),
      },
      {
        key: "noDriver",
        label: t("logisticsKpi.noDriver"),
        value: count((r) => needsOf(r).ride),
        sub: loading
          ? undefined
          : needsBooking.rangeLabel
            ? t("logisticsKpi.noDriverRangeHint", { range: needsBooking.rangeLabel })
            : t("logisticsKpi.noDriverHint", { days: needsBooking.days }),
      },
      // Radiance 261005 — patients (accepted quote or Kapar) whose flight
      // the desk still has to record.
      {
        key: "noTrip",
        label: t("logisticsKpi.noTrip"),
        value: count((r) => r.reason === "no_trip"),
        sub: loading ? undefined : t("logisticsKpi.noTripHint"),
      },
    ]
  }, [logisticsDesk, needsBooking.isLoading, needsBooking.rows, needsBooking.days, needsBooking.rangeLabel, t])

  /**
   * The desk layout's attention line (ArtDent 261015): four figures that
   * should all read zero at the end of a good day, each a click to the panel
   * it counts. Today's visits are the exception — that one is the day.
   *
   * The partner count observes the SAME query the partner panel owns (same
   * key), so React Query serves both from one request; the alerts count is
   * the reminders store's, which the sidebar bell already polls.
   */
  const intakeAllowed =
    isLoaded && hasFlag(user?.feature_flags, "affiliator_intake_panel_enabled") && can("leads.view.all")
  const intakes = useQuery({
    queryKey: ["affiliator-intakes", user?.id],
    queryFn: getAffiliatorIntakes,
    enabled: deskLayout && intakeAllowed,
    staleTime: 0,
  })
  const unreadAlerts = useRemindersStore((s) => s.unreadCount)
  const canReadBell = can("reminders.view")

  const attentionItems = useMemo<StatsMetalineItem[]>(() => {
    if (!deskLayout || logisticsDesk) return []

    const skeletonValue = <Skeleton className="h-[13px] w-12" />
    const items: StatsMetalineItem[] = []

    if (canViewAppointments) {
      items.push({
        key: "today",
        label: t("coordinatorKpi.today"),
        value: isLoading ? skeletonValue : (data.appointmentStats?.today ?? 0),
        sub: isLoading ? undefined : t("coordinatorKpi.todayHint"),
        // No schedule panel on the dashboard (owner decision 261015): the
        // day's visits are read on the calendar.
        onClick: () => router.push("/calendar"),
      })
    }
    if (needsBooking.allowed) {
      const pending = needsBooking.scoped.length
      items.push({
        key: "needsBooking",
        label: t("coordinatorKpi.needsBooking"),
        value: needsBooking.isLoading ? skeletonValue : pending,
        sub: needsBooking.isLoading
          ? undefined
          : needsBooking.rangeLabel
            ? t("coordinatorKpi.needsBookingRangeHint", { range: needsBooking.rangeLabel })
            : t("coordinatorKpi.needsBookingHint", { days: needsBooking.days }),
        accent: !needsBooking.isLoading && pending > 0 ? "warning" : "default",
        onClick: jumpTo(PANEL_ID.board),
      })
    }
    if (intakeAllowed) {
      const unopened = intakes.data?.meta.unopened
      items.push({
        key: "fromPartners",
        label: t("coordinatorKpi.fromPartners"),
        value: unopened === undefined ? skeletonValue : unopened,
        sub: unopened === undefined ? undefined : t("coordinatorKpi.fromPartnersHint"),
        accent: (unopened ?? 0) > 0 ? "warning" : "default",
        onClick: jumpTo(PANEL_ID.partners),
      })
    }
    if (canReadBell) {
      items.push({
        key: "alerts",
        label: t("coordinatorKpi.alerts"),
        value: unreadAlerts,
        sub: t("coordinatorKpi.alertsHint"),
        accent: unreadAlerts > 0 ? "warning" : "default",
        // No Alerts panel on the dashboard (owner decision 261015): the count
        // opens the Notifications page.
        onClick: () => router.push("/notifications"),
      })
    }
    return items
  }, [
    router,
    deskLayout,
    logisticsDesk,
    canViewAppointments,
    isLoading,
    data,
    needsBooking.allowed,
    needsBooking.isLoading,
    needsBooking.scoped,
    needsBooking.days,
    needsBooking.rangeLabel,
    intakeAllowed,
    intakes.data,
    canReadBell,
    unreadAlerts,
    t,
  ])

  const statItems = logisticsDesk ? transportItems : deskLayout ? attentionItems : appointmentItems

  const header = (
    <ApolloPageHeader
      eyebrow={t("page.eyebrow")}
      title={t("variants.coordinator.title")}
      stats={statItems.length > 0 ? <StatsMetaline items={statItems} /> : undefined}
      // 260907: the board's filters ride in the header's action slot, the
      // same place the front-desk variant puts them. They sat in a strip of
      // their own above the panel, which cost a whole band of chrome to show
      // three selects and read as a second header. Rendered only once the
      // board is permitted, so a user without it sees no controls that would
      // do nothing.
      actions={needsBooking.allowed ? <NeedsBookingFilters board={needsBooking} /> : undefined}
    />
  )

  if (deskLayout) {
    return (
      <ApolloPageShell>
        {header}
        <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
          {/* One full-width column: the work first, then who is new. A
              two-column desk with today's schedule beside the board was built
              and dropped the same day (owner 261015: "remove the today's
              schedule card; bring back the old layout"), and the partner table
              moved below the board right after ("the needs booking table
              first then the new from partners table") — on this desk the
              queue outranks the 261007 "newest partner patient first" order,
              which the flag-off layout keeps. */}
          <div className="flex flex-col gap-3 p-3 md:gap-4 md:p-4">
            {needsBooking.allowed && (
              /* The board keeps its definite, viewport-measured height (see
                 the one-column layout below for why). */
              <div
                id={PANEL_ID.board}
                className="flex h-[calc(100vh-19rem)] min-h-[22rem] scroll-mt-4 flex-col"
              >
                <NeedsBookingTable board={needsBooking} gutter={false} />
              </div>
            )}
            <div id={PANEL_ID.partners} className="scroll-mt-4">
              <AffiliatorIntakeCard />
            </div>
          </div>
        </div>
      </ApolloPageShell>
    )
  }

  return (
    <ApolloPageShell>
      {header}

      {/* Tighter rhythm than the generic dashboards (gap-3/p-3). This DOES
          scroll now, unlike the multi-widget board it replaced: the table
          claims the viewport and today's schedule sits below the fold on
          purpose — the desk decides who to call before it looks at the day. */}
      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto">
        <div className="flex flex-col gap-3 p-3 md:gap-4 md:p-4">
          {/* 🟥 TWO WIDGETS, in this order (260907 client decision). The board
              comes FIRST because it is the work — who has to be called and by
              when — and the schedule second because it is the day that work
              gets booked into. Reading order follows the desk's own: decide,
              then place.

              What was here before and is deliberately gone: MyTasksWidget, the
              care-team BookingWorklist (treatment starts / stages / fittings /
              follow-ups), TravelWeekCard and LabEtaCard. The last two were the
              same arrivals/departures and lab-ETA data this one table now
              carries, so keeping them would have printed every row twice; the
              other two belong to features this clinic does not run. */}

          {/* The desk's board — the SAME table the front desk gets, from the
              same three components rather than a copy: a run must not read
              differently depending on which desk opened it.

              The hook is owned HERE for the reason FrontdeskDashboard owns it:
              the filters and the table are siblings and neither can hold the
              state without the other depending on it. It self-gates, so on a
              clinic without the flag this whole block is absent, not empty —
              which is what leaves every other clinic's coordinator board
              looking exactly as it did. */}

          {/* New from partners (ArtDent 261007) — on top, by client decision:
              a patient a partner just registered is the newest thing the desk
              has not seen. Self-gating, so absent on every other clinic. */}
          <AffiliatorIntakeCard />

          {needsBooking.allowed && (
            /* The board needs a DEFINITE height — the table's internal `flex-1`
               scroller has nothing to resolve against otherwise and collapses —
               but a fixed one wasted most of a tall screen and cut the list off
               a third of the way down a laptop's. So it is measured from the
               viewport instead: everything above it (header band, page padding)
               is roughly 19rem, and `min-h` keeps it usable when a short window
               would otherwise squeeze it to a few rows.
               The page still scrolls past it to today's schedule. */
            <div className="flex h-[calc(100vh-19rem)] min-h-[22rem] flex-col">
              {/* gutter={false}: this column already carries `p-3 md:p-4`, and
                  the board's own padding on top of it put its border 12px
                  inside every sibling panel's — a visible step down the left
                  edge against Today's Schedule. */}
              <NeedsBookingTable board={needsBooking} gutter={false} />
            </div>
          )}

          {/* The day itself: whole-clinic schedule. No userId — a coordinator
              runs every chair, not their own. */}
          {canViewAppointments && (
            <TodayScheduleTimeline
              types={allowedTypes}
              refreshKey={appointmentsVersion}
              onAppointmentsChanged={bumpAppointments}
            />
          )}

          {/* Travel Desk (260914) — the trip roster, for the ONE coordinator who
              owns travel. Third and last on purpose: the two panels above are
              today, this one is the month, and a desk works the day in front of
              it before the weeks behind it.

              🟥 It gates on a PER-USER permission, not on the role. Every
              coordinator renders this line and almost none of them see a panel,
              which is the feature — the clinic divides the desk by granting
              `travel_desk.view` to one person. Self-gating, so there is no
              `allowed` check to keep in sync here and no empty panel on the
              other coordinators' boards. */}
          <TravelDeskCard />
        </div>
      </div>
    </ApolloPageShell>
  )
}
